import { useContext } from "react";
import Page, { PageDOMProps } from "../page";
import { useIntl } from "react-intl";
import UIConfiguration from "../../../ui-configuration/configuration-provider";
import EditOrganizationModal from "../../modals/edit-organization-modal";
import GroupsCard from "../../summary-cards/groups-card";
import { useHistory } from "react-router-dom";
import UsersCard from "../../summary-cards/users-card";
import InvitationsCard from "../../summary-cards/invitations-card";
import LicensesCard from "../../summary-cards/licenses-card";
import "./dashboard-view.scss";
import { Organization } from "../../../model/Organization";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import OrganizationCard from "../../summary-cards/organization-card";
import { FeedbackContainerProps } from "../../feedback/with-feedback";
import ShowFeedback from "../../show-feedback";
import RolesCard from "../../summary-cards/roles-card";

export enum ModalKeys {
  editOrganization = "edit-organization",
}
//<editor-fold desc="Props">
export interface DashboardStateProps extends FeedbackContainerProps {}
export interface DashboardModalProps {
  showModal?: { key: ModalKeys; id?: string };
  onShowModal: (key: ModalKeys, id?: string) => void;
  onHideModal: (callback?: () => void) => void;
}
export interface DashboardDOMProps extends PageDOMProps {}
export interface DashboardProps
  extends DashboardDOMProps,
    DashboardModalProps,
    DashboardStateProps {
  organization?: Organization;
}

function Dashboard(props: DashboardProps) {
  const {
    onHideModal,
    onShowModal,
    onShowFeedback,
    onHideFeedback,
    feedback,
    showModal,
    organization,
    ...other
  } = props;
  if (!organization) {
    throw new Error("No organization");
  }
  const history = useHistory();
  const intl = useIntl();
  const description = intl.formatMessage({
    defaultMessage: "Dashboard",
    description: "window meta description for the dashboard page",
  });
  const { conf } = useContext(UIConfiguration);
  const sectionsConf =
    conf.functionality && conf.functionality.sections
      ? conf.functionality.sections
      : [];
  const dashboardConf =
    conf.functionality && conf.functionality.dashboard
      ? conf.functionality.dashboard
      : {};
  // Some react rule requires key somewhere around here, but it can't actually specify where. There is no list here
  // so key should not be required. Added useless spans to keep react happy.
  const titleOrg = (
    <span key="whatever">
      {dashboardConf.editOrganization && (
        <span key="whatever2">
          <button
            key="whatever3"
            data-test-edit-organization
            className="unstyled link editOrgLink"
            onClick={() => {
              onShowModal(ModalKeys.editOrganization);
            }}
          >
            {organization ? organization.name : ""}
            <FontAwesomeIcon
              icon={"pen"}
              className={"ml-1 icon"}
              key="whatever4"
            />
          </button>
          <span key="whatever5"> </span>
        </span>
      )}
      {!dashboardConf.editOrganization &&
        (organization ? (
          <span key="whatever6">{organization.name + " "}</span>
        ) : (
          <span key="whatever7">{""}</span>
        ))}
    </span>
  );

  return (
    <Page
      data-test-dashboard-page
      id={"dashboard-page"}
      header={
        <h1 key={"header-title"}>
          {intl.formatMessage(
            {
              defaultMessage: "Welcome to {organization} orgadmin",
              description:
                "heading for the dashboard. 'organization' is the name of the organization that acts as a link for edit organization",
            },
            {
              organization: titleOrg,
            }
          )}
        </h1>
      }
      meta={{
        title: intl.formatMessage({
          defaultMessage: "Welcome to orgadmin",
          description: "window meta title for the dashboard page",
        }),
        description,
      }}
      {...other}
    >
      <ShowFeedback
        feedback={feedback}
        idPrefix={"dashboard_"}
        onHideFeedback={onHideFeedback}
      />
      {(sectionsConf.length ||
        dashboardConf.showOrganization ||
        dashboardConf.editOrganization) && (
        <div className={"card-deck mt-2"}>
          {dashboardConf.showOrganization && (
            <OrganizationCard
              moreAction={
                dashboardConf.editOrganization
                  ? () => {
                      onShowModal(ModalKeys.editOrganization);
                    }
                  : null
              }
              key={"organization"}
            >
              <>
                {dashboardConf.editOrganization && (
                  <EditOrganizationModal
                    onShowFeedback={onShowFeedback}
                    show={
                      showModal && showModal.key === ModalKeys.editOrganization
                        ? true
                        : false
                    }
                    onClose={onHideModal}
                  />
                )}
              </>
            </OrganizationCard>
          )}
          {sectionsConf.length > 0
            ? sectionsConf.map((s) => {
                let retVal;
                if (s === "groups") {
                  retVal = (
                    <GroupsCard
                      moreAction={() => {
                        history.push("/groups");
                      }}
                      key={s}
                    />
                  );
                } else if (s === "roles") {
                  retVal = (
                    <RolesCard
                      moreAction={() => {
                        history.push("/roles");
                      }}
                      key={s}
                    />
                  );
                } else if (s === "users") {
                  retVal = (
                    <UsersCard
                      moreAction={() => {
                        history.push("/users");
                      }}
                      key={s}
                    />
                  );
                } else if (s === "invitations") {
                  retVal = (
                    <InvitationsCard
                      moreAction={() => {
                        history.push("/invitations");
                      }}
                      key={s}
                    />
                  );
                } else if (s === "licenses") {
                  retVal = (
                    <LicensesCard
                      moreAction={() => {
                        history.push("/licenses");
                      }}
                      key={s}
                    />
                  );
                }
                return retVal;
              })
            : undefined}
        </div>
      )}
      {!dashboardConf.showOrganization && dashboardConf.editOrganization && (
        <EditOrganizationModal
          key={"edit-organization"}
          onShowFeedback={onShowFeedback}
          show={
            showModal && showModal.key === ModalKeys.editOrganization
              ? true
              : false
          }
          onClose={onHideModal}
        />
      )}
    </Page>
  );
}
export default Dashboard;
